"use client"

import type React from "react"

import { useState, useRef, useEffect } from 'react'
import { X, Send } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import Link from "next/link"

interface WaitlistModalProps {
  isOpen: boolean
  onClose: () => void
  /** Fired once a waitlist entry submits successfully — used for campaign
   *  conversion tracking. Optional, so existing usages are unaffected. */
  onSuccess?: () => void
}

export default function WaitlistModal({ isOpen, onClose, onSuccess }: WaitlistModalProps) {
  const [formData, setFormData] = useState({
    clinicName: "",
    contactName: "",
    email: "",
    phone: "",
    message: ""
  })
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)

  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    console.log("Form submit triggered")
    console.log("Form data:", formData)
    
    // Check if required fields are filled
    if (!formData.clinicName || !formData.contactName || !formData.email) {
      console.log("Missing required fields:", {
        clinicName: !!formData.clinicName,
        contactName: !!formData.contactName,
        email: !!formData.email
      })
      setError("Please fill in all required fields")
      return
    }
    
    if (!acceptedTerms) {
      setError("You must accept the Terms of Service to join the waitlist")
      return
    }
    
    setLoading(true)
    setError("")
    setSuccess(false)

    console.log("Submitting waitlist form:", formData)

    try {
      console.log("Making fetch request to /api/waitlist")
      const response = await fetch('/api/waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      console.log("Response status:", response.status)
      console.log("Response headers:", Object.fromEntries(response.headers.entries()))
      
      const data = await response.json()
      console.log("Response data:", data)

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit waitlist entry')
      }

      console.log("Success! Setting success state")
      onSuccess?.()
      setSuccess(true)
      setFormData({
        clinicName: "",
        contactName: "",
        email: "",
        phone: "",
        message: ""
      })
      
      // Close modal after 6 seconds
      setTimeout(() => {
        onClose()
        setSuccess(false)
      }, 6000)
    } catch (error) {
      console.error("Error submitting waitlist (client):", error);
      
      // Send error notification
      // await ClientErrorHandler.handleFormError(error, {
      //   component: "WaitlistModal",
      //   operation: "Form Submission",
      //   additionalData: {
      //     clinicName: formData.clinicName,
      //     contactName: formData.contactName,
      //     email: formData.email,
      //   },
      // });
      
      setError(error instanceof Error ? error.message : "Error submitting form. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed z-[99] inset-0 bg-black/70 flex items-center sm:items-start justify-center z-50 p-4 sm:p-[20px]">
      <div ref={modalRef} className="bg-white rounded-[8px] shadow-lg w-full max-w-[601px] relative animate-fadeIn max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute font-semibold right-4 top-4 text-[#002e65] z-10"
          aria-label="Close"
        >
          <X size={22} />
        </button>

        <div className="p-[30px] sm:p-[20px]">
          <div className="mb-[30px] sm:mb-[20px]">
            <h2 className="text-[#002e65] text-[40px] sm:text-[30px] leading-[1.2] text-left font-semibold mb-[30px] sm:mb-[20px]">Join our waitlist</h2>
            <p className="text-[#002e65] text-[18px] sm:text-[16px] leading-[1.5] text-left">
              Be among the first to experience our comprehensive pet aftercare platform. Join our waitlist and we'll notify you when we launch.
            </p>
          </div>

          {success ? (
            <div className="text-center py-8">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Send className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <h3 className="text-green-800 text-xl font-semibold mb-2">Thank you!</h3>
                <p className="text-green-700">Your waitlist entry has been submitted successfully. We'll be in touch soon!</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 sm:space-y-[20px]">
                <div>
                  <Label className="block text-left text-[16px] font-semibold text-[#002e65] mb-[5px]" htmlFor="clinicName">Clinic name *</Label>
                  <Input
                    className="w-full p-[15px] sm:py-[10px] sm:px-[15px] h-[54px] sm:h-[44px] border border-[#002e65] text-[#002e65] rounded-[8px] focus:ring-0 focus-visible:ring-0 focus:ring-1 focus:ring-[#002e65] !placeholder-[#002e65] text-[16px] placeholder:italic"
                    id="clinicName"
                    name="clinicName"
                    placeholder="Enter your clinic name"
                    value={formData.clinicName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label className="block text-left text-[16px] font-semibold text-[#002e65] mb-[5px]" htmlFor="contactName">Contact name *</Label>
                  <Input
                    className="w-full p-[15px] sm:py-[10px] sm:px-[15px] h-[54px] sm:h-[44px] border border-[#002e65] text-[#002e65] rounded-[8px] focus:ring-0 focus-visible:ring-0 focus:ring-1 focus:ring-[#002e65] !placeholder-[#002e65] text-[16px] placeholder:italic"
                    id="contactName"
                    name="contactName"
                    placeholder="Enter contact name"
                    value={formData.contactName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label className="block text-left text-[16px] font-semibold text-[#002e65] mb-[5px]" htmlFor="email">Email address *</Label>
                  <Input
                    type="email"
                    className="w-full p-[15px] sm:py-[10px] sm:px-[15px] h-[54px] sm:h-[44px] border border-[#002e65] text-[#002e65] rounded-[8px] focus:ring-0 focus-visible:ring-0 focus:ring-1 focus:ring-[#002e65] !placeholder-[#002e65] text-[16px] placeholder:italic"
                    id="email"
                    name="email"
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label className="block text-left text-[16px] font-semibold text-[#002e65] mb-[5px]" htmlFor="phone">Phone number</Label>
                  <Input
                    type="tel"
                    className="w-full p-[15px] sm:py-[10px] sm:px-[15px] h-[54px] sm:h-[44px] border border-[#002e65] text-[#002e65] rounded-[8px] focus:ring-0 focus-visible:ring-0 focus:ring-1 focus:ring-[#002e65] !placeholder-[#002e65] text-[16px] placeholder:italic"
                    id="phone"
                    name="phone"
                    placeholder="Enter your phone number"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <Label className="block text-left text-[16px] font-semibold text-[#002e65] mb-[5px]" htmlFor="message">Message</Label>
                  <Textarea
                    className="w-full p-[15px] sm:py-[10px] sm:px-[15px] min-h-[120px] border border-[#002e65] text-[#002e65] rounded-[8px] focus:ring-0 focus-visible:ring-0 focus:ring-1 focus:ring-[#002e65] !placeholder-[#002e65] text-[16px] placeholder:italic resize-none"
                    id="message"
                    name="message"
                    placeholder="Tell us about your clinic and any specific needs..."
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                  />
                </div>

                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="terms"
                    checked={acceptedTerms}
                    onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
                    className="mt-1"
                  />
                  <label
                    htmlFor="terms"
                    className="text-sm text-[#002e65] leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    I agree to the{" "}
                    <Link href="/terms" className="text-[#004da9] hover:text-[#0055a4] underline" target="_blank">
                      Terms of Service
                    </Link>
                  </label>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                )}

                <div className="flex flex-wrap justify-end gap-[20px] !mt-[40px] sm:!mt-[30px]">
                  <Button className="block sm:hidden px-[30px] text-[18px] h-[56px] leading-[1.4] py-[15px] border border-[#004da9] text-[#004da9] rounded-full hover:text-white hover:bg-[#004da9]" type="button" variant="outline" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button 
                    className="px-[30px] text-[18px] h-[56px] leading-[1.4] border border-[#004da9] py-[15px] bg-[#004da9] text-white rounded-full hover:bg-white hover:text-[#004da9]" 
                    type="submit"
                    disabled={loading || !acceptedTerms}
                    onClick={() => console.log("Submit button clicked")}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Join waitlist
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

